import React, { useContext, useState, useEffect, useRef } from 'react';
import { Environment } from 'src/Environment';
import { Form, Field, Formik } from 'formik';
import { Redirect } from 'react-router-dom';

import {
  onboardingAgentOtpRoute,
  onboardingBusinessTypeReskinRoute,
  onboardingContinueReferenceRoute,
} from 'src/routing/routes';
import { phoneNumberMaskSpaced, stripWhitespace } from 'src/helpers/formatting';
import {
  isValidName,
  isValidEmail,
  isValidMobileNumber,
  hasNoSpecialCharacters,
  validateEmailTradeRoot,
  isValidEmailTradeRoot,
} from 'src/helpers/validation';
import MerchantAgreementLink from 'src/containers/onboarding/merchant-agreement-link/merchant-agreement-link';
import { FloatingInput, PrivacyNoticeLink } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { SalesforceContext, Steps, CustomerTypes } from 'src/context/salesforce-context';
import TealiumEvents from 'src/helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { OnboardingFormBackground } from '../onboarding-form-background/onboarding-form-background';
import { HelpFloatingActionButtonOnboarding, ShapesDecorativeBorder } from 'src/components';
import { requestSmsOtp } from 'src/services/otp-service';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { initialiseCompressionLibrary } from 'src/helpers/jpeg-compression';
import { getValue, urlQueryParams } from 'src/helpers/custom-url-search-param';
import './lets-get-started.scss';

const CAPTCHACONTAINERNAME = 'captchaContainer';

export default function LetsGetStarted() {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const [inputDanger, setInputDanger] = useState({
    'first-names': false,
    email: false,
    'mobile-number': false,
  });
  const [, setError] = useState('');
  const [redirectToRoute, setRedirectToRoute] = useState('');
  const [notABot, setNotABot] = useState(false);
  const { lead, setLead, save, clearCache, setCustomerType, customerType, setOtpRetriesExceeded } =
    useContext(SalesforceContext);
  const handleTechnicalError = useTechnicalErrorHandler();
  const topOfPage = useRef('top');

  const FIRST_NAME_MAX_LENGTH = 40;

  const updateLead = (newLead) => {
    setLead({
      ...lead,
      firstNames: newLead.firstNames,
      email: newLead.email,
      mobileNumber: newLead.mobileNumber,
      dealerId: newLead.dealerId,
      agentId: newLead.agentId,
      salesOrg: newLead.salesOrg,
    });
  };

  useEffect(() => {
    clearCache();
    if (topOfPage.current) {
      topOfPage.current.scrollIntoView(false);
    }
    initialiseCompressionLibrary();
  }, []);

  useEffect(() => {
    if (inMiniProgram) {
      MiniAppEvents.sendMessage({
        messageType: 'get_user_info',
      });

      MiniAppEvents.listenForMessage((observer) => {
        const message = MiniAppEvents.readMessage();
        if (message.type === 'get_user_info') {
          if (message.data) {
            updateLead(message.data);
          }
          observer.disconnect();
        }
      });
    }
  }, []);

  useEffect(() => {
    const queryParams = urlQueryParams(window.location.search);

    const { sales_org, sales_agent } = queryParams;

    if (sales_org !== undefined && sales_agent !== undefined) {
      updateLead({
        ...lead,
        agentId: sales_agent,
        salesOrg: sales_org,
      });
    }

    const customerTypeParam = getValue(queryParams, 'customerType');
    const updatedCustomerType = !!customerTypeParam ? customerTypeParam : CustomerTypes.normal;
    setCustomerType(updatedCustomerType);

    const customerTypeCalculated = !!customerType ? customerType : updatedCustomerType;
    const assistantId = lead.dealerId || lead.agentId || '';

    TealiumEvents.letsGetStartedPageLoadFormStartCombined(customerTypeCalculated, assistantId);

    if (!Environment.current?.disabledFeatures?.captcha) {
      const script = document.createElement('script');
      script.src = `${Environment.current?.captchaURL}?render=explicit`;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      checkForRecaptcha();
    } else {
      setNotABot(true);
    }
  }, []);

  const continueLink = () => (
    <div className="center-text back-link">
      <a
        onClick={() => {
          TealiumEvents.letsGetStartedClickContinueApplication(
            onboardingContinueReferenceRoute,
            customerType,
          );
          setRedirectToRoute(onboardingContinueReferenceRoute);
        }}
        automationid="Onboarding_Lets_Get_Started_Anchor_Link_Continue_Your_Application"
      >
        Go to saved application
        <span className="icon-menu-chevron-right has-padding-left-6 is-size-7" />
      </a>
    </div>
  );

  const checkForRecaptcha = () => {
    window.setTimeout(() => {
      if ('grecaptcha' in window && typeof window.grecaptcha.render === 'function') {
        window.grecaptcha.render(CAPTCHACONTAINERNAME, {
          sitekey: Environment.current?.captchaKey,
          callback: () => setNotABot(true),
        });
      } else {
        checkForRecaptcha();
      }
    }, 200);
  };

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  const errorMessages = {
    normal: {
      firstName: 'The first name entered is invalid',
      email: 'The email entered is invalid',
      number: 'The mobile number entered is invalid',
    },
    dealer: {
      firstName: 'The first name entered is invalid',
      email: 'The email entered is invalid',
      number: 'The contact number entered is invalid',
    },
    agent: {
      firstName: 'The first name entered is invalid',
      email: 'The email entered is invalid',
      number: 'The mobile number entered is invalid',
    },
  };

  const formContent = () => {
    return (
      <div className="form">
        <section className="lets-get-started-form">
          <Formik
            initialValues={{
              firstNames: lead?.firstNames,
              email: lead?.email,
              mobileNumber: lead?.mobileNumber,
              dealerId: lead?.dealerId,
              agentId: lead?.agentId,
            }}
            validate={validateForm}
            onSubmit={submitForm}
            validateOnChange={true}
            validateOnBlur={false}
            enableReinitialize={true}
          >
            {({ isSubmitting, values }) => (
              <Form>
                <div className="background-container">
                  <div className="top-background-container">
                    <div className="columns is-gapless is-centered is-mobile">
                      <div className="column is-four-fifths">
                        <div className="has-text-centered has-padding-top-2">
                          <p className="heading-four has-text-purple-grey" ref={topOfPage}>
                            Let's get started!
                          </p>
                        </div>
                        {customerType === CustomerTypes.dealer && (
                          <Field
                            name="dealerId"
                            type="text"
                            component={MiniAppFocusWrapper}
                            innerComponent={FloatingInput}
                            spacing="has-margin-top-5"
                            className="top-field"
                            fieldType="standard"
                            placeholder="Dealer ID"
                            automationid="Onboarding_Lets_Get_Started_Field_DealerId"
                            autoFocus
                          />
                        )}
                        <Field
                          name="firstNames"
                          type="text"
                          placeholder={
                            customerType !== CustomerTypes.dealer ? 'First name' : 'Merchant name'
                          }
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          fieldType="standard"
                          className={customerType === CustomerTypes.dealer ? '' : 'top-field'}
                          spacing={customerType === CustomerTypes.dealer ? '' : 'has-margin-top-5'}
                          maxLength={FIRST_NAME_MAX_LENGTH}
                          automationid="Onboarding_Lets_Get_Started_Field_First_Names"
                          autoFocus
                        />
                        <Field
                          name="email"
                          type="text"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          fieldType="standard"
                          placeholder={
                            customerType !== CustomerTypes.dealer
                              ? 'Email address'
                              : 'Merchant email address'
                          }
                          automationid="Onboarding_Lets_Get_Started_Field_Email"
                        />
                        <Field
                          name="mobileNumber"
                          mask={phoneNumberMaskSpaced}
                          id="mobileNumber"
                          automationid="Onboarding_Lets_Get_Started_Field_Mobile_Number"
                          placeholder={
                            customerType !== CustomerTypes.dealer
                              ? 'Mobile number'
                              : 'Merchant contact number'
                          }
                          className="bottom-field"
                          type="tel"
                          guide={!inMiniProgram}
                          fieldType="masked"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                        />
                      </div>
                    </div>
                    <div className="captcha has-padding-bottom-4">
                      <div id={CAPTCHACONTAINERNAME} />
                    </div>
                  </div>
                  <div className="columns is-mobile is-centered">
                    <div className="column is-four-fifths">
                      <div className="columns is-mobile">
                        <div className="column is-full">
                          <MerchantAgreementLink className="merchant-agreement has-padding-bottom-6" />
                          <PrivacyNoticeLink />
                          <button
                            className="button is-primary is-rounded is-fullwidth button-text has-margin-bottom-6"
                            type="submit"
                            automationid="Onboarding_Lets_Get_Started_Button_Continue"
                            disabled={isSubmitting || !isFormValid(values)}
                          >
                            Get started
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {!Environment.current?.disabledFeatures.onboardingContinueApplication
                  ? continueLink()
                  : null}
              </Form>
            )}
          </Formik>
        </section>
      </div>
    );
  };

  return (
    <div className="lets-get-started">
      <OnboardingFormBackground
        isAgent={customerType !== CustomerTypes.normal && customerType !== CustomerTypes.pos}
        customerType={customerType}
        setCustomerType={setCustomerType}
      >
        <ShapesDecorativeBorder>{formContent()}</ShapesDecorativeBorder>
        <HelpFloatingActionButtonOnboarding customerType={customerType} />
      </OnboardingFormBackground>
    </div>
  );

  async function submitForm(values) {
    TealiumEvents.letsGetStartedClickGetStartedAndFormEndCombined(
      onboardingBusinessTypeReskinRoute,
      customerType,
    );

    if (customerType === CustomerTypes.agent && !Environment.current?.disabledFeatures.agentOtp) {
      setOtpRetriesExceeded(false);
      const { error } = await requestSmsOtp(values.mobileNumber);

      switch (error) {
        case null:
          toastMessageServiceSingleton.addToast({
            message: `We sent the customer an OTP via SMS`,
            type: 'info',
          });
          await save(Steps.LETS_GET_STARTED, null, true);
          setRedirectToRoute(onboardingAgentOtpRoute);
          break;
        case 'Maximum retry limit exceeded. Locked for 24 hours.':
          setOtpRetriesExceeded(true);
          setRedirectToRoute(onboardingAgentOtpRoute);
          break;
        default:
          handleTechnicalError('unrecognized error received in response to OTP get request');
          break;
      }
    } else {
      await save(Steps.LETS_GET_STARTED);
      setRedirectToRoute(onboardingBusinessTypeReskinRoute);
    }
  }

  function isFormValid(values) {
    let dealerValid = true;
    if (customerType === CustomerTypes.dealer) {
      dealerValid = !!hasNoSpecialCharacters(values.dealerId) && values.dealerId !== '';
    }
    return (
      isValidName(values.firstNames) &&
      isValidEmail(values.email) &&
      isValidEmailTradeRoot(values.email) &&
      isValidMobileNumber(stripWhitespace(values.mobileNumber)) &&
      dealerValid &&
      notABot &&
      !(
        !values.firstNames ||
        !values.email ||
        !values.mobileNumber ||
        values.firstNames === '' ||
        values.email === '' ||
        values.mobileNumber === ''
      )
    );
  }

  function validateForm(values) {
    const errors = {};
    let newInputDanger = inputDanger;

    values.mobileNumber = stripWhitespace(values.mobileNumber);

    const invalidFirstName = !isValidName(values.firstNames);
    newInputDanger['first-names'] = invalidFirstName;
    if (invalidFirstName) {
      errors.firstNames = values.firstNames ? errorMessages[customerType].firstName : null;
    }

    const emailTradeRootValidationError = validateEmailTradeRoot(values.email);

    if (emailTradeRootValidationError) {
      newInputDanger['email'] = true;
      errors.email = values.email ? emailTradeRootValidationError : null;
    } else {
      const invalidEmail = !isValidEmail(values.email);
      newInputDanger['email'] = invalidEmail;
      if (invalidEmail) {
        errors.email = values.email ? errorMessages[customerType].email : null;
      }
    }

    const invalidMobileNumber = !isValidMobileNumber(values.mobileNumber);
    newInputDanger['mobile-number'] = invalidMobileNumber;
    if (invalidMobileNumber) {
      errors.mobileNumber = values.mobileNumber ? errorMessages[customerType].number : null;
    }

    if (customerType === CustomerTypes.dealer) {
      const invalidDealerId = !hasNoSpecialCharacters(values.dealerId);
      newInputDanger['dealerId'] = invalidDealerId;
      if (invalidDealerId) {
        errors.dealerId = values.dealerId ? 'The dealer ID entered is invalid' : null;
      }
    }

    setInputDanger(newInputDanger);
    let fieldsNotCompleted = !values.firstNames || !values.email || !values.mobileNumber;

    if (customerType === CustomerTypes.dealer) {
      fieldsNotCompleted = !!fieldsNotCompleted || !values.dealerId;
    }

    if (fieldsNotCompleted) {
      setError('Please complete all the required fields.');
    } else {
      setError('');
    }

    let anyErrors = false;
    Object.keys(errors).forEach((key) => {
      if (errors[key] !== null) {
        anyErrors = true;
      }
    });
    if (!anyErrors) {
      updateLead({ ...lead, ...values });
    }
    return errors;
  }
}
